import { IFilterService, IPromise, IQService, IRootScopeService, IScope } from "angular";
import { StateService } from "@uirouter/angularjs";
import { AssigneeActions, IAssigneeIdMap, IAssigneesState, IAssigneesStoreState } from "@gtmhub/assignees";
import { IAuditLog, IGridAuditLog } from "@gtmhub/configuration";
import { AuditLogService } from "@gtmhub/configuration/services/audit-log-service";
import {
  auditLogFilterDefinitions,
  buildActionText,
  buildDisplayType,
  columnsFieldLabelMap,
  defaultCustomFieldGHGridColumns,
  generateDefaultCustomFieldsFilters,
  mergeParamTypesAndActions,
} from "@gtmhub/configuration/utils/audit-log-utils";
import { IStateInit } from "@gtmhub/core/routing";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { convertColumnToDefinition, convertColumnToSortingDefinition } from "@gtmhub/customFields";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { IRolesState, IRolesStoreState, RolesActions } from "@gtmhub/roles";
import {
  GridColumnDefinitions,
  GridFilterDefinitions,
  GridSortingDefinitions,
  IGridFiltersChange,
  IGridSortingDefinition,
  IGridSortingRule,
  OrFiltersGroup,
  getPersistedScreenSettings,
  setPersistedScreenSettings,
  toGridDefinitionMap,
  toRestLayerRequest,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { IGHGridColumn } from "@gtmhub/shared/components/grid/grid.component";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { IUser, UserService } from "@gtmhub/users";
import { IdMap, NameMap, capitalize, toIdMap, toKeyMap } from "@gtmhub/util";

export interface IAuditLogScope extends IScope, IExtendedRootScopeService {
  indicators: {
    loading?: boolean;
    initialLoad?: boolean;
  };
  assigneesIdMap: IAssigneeIdMap;
  assigneesState: IAssigneesState;
  rolesState: IRolesState;
  auditLogs: IAuditLog[];
  gridAuditLogs: IGridAuditLog[];
  filters: OrFiltersGroup[];
  filterDefinitions: GridFilterDefinitions;
  columnDefinitions: GridColumnDefinitions;
  gridColumns: IGHGridColumn[];
  sortingDefinitions: GridSortingDefinitions;
  sortingRules: IGridSortingRule[];
  columns: IGHGridColumn[];
  currentPage: number;
  totalPages: number;

  onPageChange(page: number): void;

  showNoFilterResults: boolean;

  filtersChange(change: IGridFiltersChange): void;

  sortingChange(): void;

  columnsChange(): void;
}

export interface IAuditLogStateParams {
  page?: string;
}

const SCREEN_PATH = ["audit-log"];
const AUDIT_LOG_GRID_COLUMNS = "auditLogGridColumns";
const PAGING_SIZE = 20;
const FIRST_PAGE = 1;

const bindStateToScope = (state: IAssigneesStoreState & IRolesStoreState) => ({
  assigneesIdMap: state.assignees.map,
  assigneesState: state.assignees,
  accountRoles: state.roles.getRolesExcludeTeamRoles(),
  rolesState: state.roles,
});

export class AuditLogCtrl implements IStateInit {
  private columnsFieldMap: NameMap<IGHGridColumn>;
  private auditLogsTotalCount: number;
  private usersMap: IdMap<IUser>;

  public static $inject = [
    "$q",
    "$scope",
    "$rootScope",
    "$state",
    "$stateParams",
    "$ngRedux",
    "$filter",
    "AssigneeActions",
    "RolesActions",
    "UserService",
    "AuditLogService",
    "UIErrorHandlingService",
  ];

  constructor(
    private $q: IQService,
    private $scope: IAuditLogScope,
    private $rootScope: IRootScopeService & ITraceRootScopeService,
    private $state: StateService,
    private $stateParams: IAuditLogStateParams,
    private $ngRedux: INgRedux,
    private $filter: IFilterService,
    private assigneeActions: AssigneeActions,
    private rolesActions: RolesActions,
    private userService: UserService,
    private auditLogService: AuditLogService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.$scope.indicators = {
      initialLoad: true,
    };

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);

    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.rolesActions.getRolesIfMissing());

    this.$scope.onPageChange = this.onPageChange;
    this.$scope.filtersChange = this.filtersChange;
    this.$scope.sortingChange = this.sortingChange;
    this.$scope.columnsChange = this.columnsChange;
  }

  stateInit(): IPromise<unknown> {
    this.initColumns();
    this.initFilters();
    this.initSorting();

    const paramsPage = Number(this.$stateParams.page);
    this.$scope.currentPage = isNaN(paramsPage) ? FIRST_PAGE : paramsPage;

    const usersPromise = this.userService.getUsersV2({ fields: "id,roles,accountId" });
    const auditLogOptions = this.auditLogService.getOptions();

    return this.$q.all([usersPromise, auditLogOptions]).then(
      ([users, auditLogOptions]) => {
        this.$scope.filterDefinitions = auditLogFilterDefinitions(auditLogOptions, this.$rootScope.edition.auditTrailMonthsLimit);
        this.usersMap = toIdMap(users.items);

        return this.$scope
          .watchUntil(() => this.$scope.assigneesState.isFetched && this.$scope.rolesState.isFetched)
          .then(() => {
            return this.getAuditLog();
          });
      },
      (err) => {
        this.uiErrorHandlingService.handleModal(err);
      }
    );
  }

  private getAuditLog = (): IPromise<void> => {
    const skip = (this.$scope.currentPage - 1) * PAGING_SIZE;
    this.$scope.indicators.loading = true;

    const request = toRestLayerRequest({ sorting: this.$scope.sortingRules });
    const filterMap = toKeyMap("fieldName", this.$scope.filters[0]);
    const params = {
      startDate: filterMap["dateTime"]?.value["startDate"] as string,
      endDate: filterMap["dateTime"]?.value["endDate"] as string,
      ownerIds: (filterMap["user"]?.value as string[])?.join(","),
      action: (filterMap["action"]?.value as string[])?.join(","),
      type: (filterMap["type"]?.value as string[])?.join(","),
      skip: skip,
      take: PAGING_SIZE,
      sort: request.sort,
    };

    mergeParamTypesAndActions(params);
    return this.auditLogService.getAuditLog<IAuditLog>(params).then(
      (result) => {
        this.auditLogsTotalCount = result.count;

        this.setTotalPages(this.auditLogsTotalCount);
        this.$scope.auditLogs = result.items || [];

        this.setGridAuditLogs();
        this.$scope.showNoFilterResults = this.auditLogsTotalCount === 0;
        this.$scope.indicators.loading = false;
        this.$scope.indicators.initialLoad = false;
      },
      () => {
        this.auditLogsTotalCount = 0;

        this.setTotalPages(this.auditLogsTotalCount);
        this.$scope.auditLogs = [];

        this.setGridAuditLogs();
        this.$scope.showNoFilterResults = this.auditLogsTotalCount === 0;
        this.$scope.indicators.loading = false;
        this.$scope.indicators.initialLoad = false;
      }
    );
  };

  private initColumns = (): void => {
    this.$scope.columns = this.getAuditLogGHGridColumns(SCREEN_PATH);
    this.$scope.columns.forEach((col) => {
      col.label = capitalize(localize(columnsFieldLabelMap[col.fieldName]));
    });
    this.columnsFieldMap = toKeyMap("fieldName", this.$scope.columns);
    this.$scope.columnDefinitions = this.createColumnDefinitions(this.$scope.columns);
    this.$scope.gridColumns = this.$scope.columns.filter((column: IGHGridColumn): boolean => column.visible);
  };

  private getAuditLogGHGridColumns = (screenPath: string[]): IGHGridColumn[] => {
    const defaultColumns = defaultCustomFieldGHGridColumns();
    const persistedColumns = getPersistedScreenSettings(screenPath, AUDIT_LOG_GRID_COLUMNS);

    if (persistedColumns && persistedColumns.length > 0) {
      return persistedColumns;
    } else {
      setPersistedScreenSettings(screenPath, AUDIT_LOG_GRID_COLUMNS, defaultColumns);
      return defaultColumns;
    }
  };

  private initFilters = (): void => {
    this.$scope.filters = generateDefaultCustomFieldsFilters();
  };

  private initSorting = (): void => {
    this.$scope.sortingDefinitions = this.createSortingDefinitions();
    this.$scope.sortingRules = [{ fieldName: "dateTime", reversed: true }];
  };

  private createColumnDefinitions = (columns: IGHGridColumn[]): GridColumnDefinitions => {
    return toGridDefinitionMap(columns.map((column) => convertColumnToDefinition(column, this.$filter)));
  };

  private createSortingDefinitions = (): Record<string, IGridSortingDefinition> => {
    return toGridDefinitionMap(
      this.$scope.columns.filter((column) => column.fieldName === "dateTime" && column.visible).map((column) => convertColumnToSortingDefinition(column, this.$filter))
    );
  };

  private filtersChange = (change: IGridFiltersChange): void => {
    this.$rootScope.traceAction("change_audit_log_filters", () => {
      if (change.initial) {
        return;
      } else {
        setPersistedScreenSettings(SCREEN_PATH, "filters", this.$scope.filters);
        this.setCurrentPage(FIRST_PAGE);
      }
    });
  };

  private sortingChange = (): void => {
    this.$rootScope.traceAction("change_audit_log_sorting", () => {
      this.setCurrentPage(FIRST_PAGE);
    });
  };

  private columnsChange = (): void => {
    this.$rootScope.traceAction("change_audit_log_columns", () => {
      const gridColFieldNames = this.$scope.gridColumns.map((gridCol) => gridCol.fieldName);
      const columnsToHide = this.$scope.columns.filter((col) => !gridColFieldNames.includes(col.fieldName));
      columnsToHide.forEach((hiddenCol) => (hiddenCol.visible = false));

      const newColumnOrder = this.$scope.gridColumns.map((gridColumn) => ({
        ...this.columnsFieldMap[gridColumn.fieldName],
        visible: true,
      }));

      newColumnOrder.push(...columnsToHide);

      this.$scope.columns = newColumnOrder;

      setPersistedScreenSettings(SCREEN_PATH, AUDIT_LOG_GRID_COLUMNS, this.$scope.columns);
      this.$scope.sortingDefinitions = this.createSortingDefinitions();

      if (this.areSortingRulesMissingFromSortingDefinitions()) {
        this.$scope.sortingRules = [{ fieldName: "dateTime", reversed: true }];
        this.sortingChange();
      }
    });
    this.setCurrentPage(FIRST_PAGE);
  };

  private areSortingRulesMissingFromSortingDefinitions = (): boolean => {
    const rulesNotInDefinitions = this.$scope.sortingRules.filter((sortingRule) => !(sortingRule.fieldName in this.$scope.sortingDefinitions));
    return rulesNotInDefinitions.length > 0;
  };

  private setTotalPages = (totalCount: number) => {
    this.$scope.totalPages = Math.ceil(totalCount / PAGING_SIZE);

    if (this.$scope.totalPages && this.$scope.totalPages < this.$scope.currentPage) {
      this.setCurrentPage(this.$scope.totalPages);
    }
  };

  private setCurrentPage = (page?: number) => {
    this.$scope.currentPage = page || FIRST_PAGE;
    this.goToCurrentPage();
  };

  private goToCurrentPage = () => {
    this.$state.go(".", { page: this.$scope.currentPage }, { notify: false });
    this.getAuditLog();
  };

  private onPageChange = (page: number): void => {
    this.setCurrentPage(page);
  };

  private setGridAuditLogs = () => {
    this.$scope.gridAuditLogs = this.$scope.auditLogs.map((log: IAuditLog) => {
      return {
        id: `${log.kind}-${log.created}-${log.ownerId}-${log.target}`,
        dateTime: log.created,
        role: this.buildRoles(log.ownerId),
        user: this.$scope.assigneesIdMap[log.ownerId],
        action: buildActionText(log.kind),
        item: this.buildItemText(log),
        type: log.target,
        displayType: buildDisplayType(log.target),
        targetId: log.targetId,
        description: this.buildDescription(log.kind, log.target, log.changes),
        isDeleted: log.isDeleted,
        currentUserHasAccess: log.currentUserHasAccess,
      };
    });
  };

  private buildItemText = (log: IAuditLog): string => {
    if (log.currentUserHasAccess !== undefined && !log.currentUserHasAccess) {
      return localize("private");
    } else {
      return log.targetName ? log.targetName : "Item link";
    }
  };

  private buildRoles = (ownerId: string): string => {
    if (ownerId && this.usersMap[ownerId]) {
      return this.usersMap[ownerId].roles
        .filter((role) => role.team === null)
        .map((role) => role.name)
        .join(",");
    } else {
      return "";
    }
  };

  private capText = (text: string): string => {
    return text
      .replace("_", " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  private buildDescription = (kind: string, target: string, changes): string => {
    if (!changes) {
      return "";
    }

    if (target === "whiteboard") {
      if (kind === "object created") {
        return localize("item_created_on_canvas");
      } else if (kind === "object modified") {
        return localize("item_modified_on_canvas");
      } else if (kind === "object deleted") {
        return localize("item_deleted_on_canvas");
      }
    } else if (target === "plugin") {
      return `${localize("marketplace_app")} ${this.capText(kind)}`;
    } else if (target === "plugin version") {
      return `${localize("developer_portal_app_in_dev")} ${this.capText(kind)}`;
    }

    return `${this.buildChangedFields(changes)} ${this.capText(kind)}`;
  };

  private buildChangedFields = (changes): string => {
    return Object.keys(changes)
      .map((key) => {
        const upperCaseChar = key.charAt(0).toUpperCase();
        return upperCaseChar
          .concat(key.slice(1))
          .split(/(?=[A-Z])/)
          .join(" ");
      })
      .join("; ");
  };
}
